import { TriggerForPersonFrequencyEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { captureMessage, nativeCrash } from '@sentry/react-native';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { Button, Switch } from 'react-native-paper';

import { useTriggerStatisticsEmailDebug } from '@/features/api/hooks/grapevine-admin-statistics-debug/useTriggerStatisticsEmailDebug';
import { useDebugStore } from '@/features/experiments/store/useDebugStore';
import { usePopup } from '@/features/popup/provider/usePopup';
import { useAppSettingStore } from '@/features/profile-setting/store/useAppSettingStore';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationButton } from '@/navigation/components/NavigationButton';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { useBadgeStore } from '@/shared/store/useBadgeStore';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      padding: 16,
      gap: 16,
      backgroundColor: theme.colors.background,
      alignItems: 'center',
      justifyContent: 'center'
    },
    button: {
      borderRadius: 4
    },
    buttonRow: {
      gap: 8,
      flexWrap: 'wrap',
      flexDirection: 'row',
      justifyContent: 'center'
    },
    map: {
      width: '100%',
      height: 400
    },
    themeColors: {
      flexDirection: 'column'
    },
    colorPropertyContainer: {
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      flexDirection: 'row',
      margin: 1,
      gap: 4
    },
    colorPropertyText: {
      color: theme.colors.onBackground
    }
  });

export const ExperimentsScreen: FC = () => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation([
    LocalizationNameSpace.EXPERIMENTS,
    LocalizationNameSpace.AUTHORIZATION
  ]);

  const updateBadges = useBadgeStore((state) => state.updateBadge);
  const clearBadges = useBadgeStore((state) => state.reset);

  const debugMessage = useDebugStore((state) => state.debugMessage);

  const { showToast } = usePopup();
  const triggerStatisticsEmail = useTriggerStatisticsEmailDebug();
  const triggerStatisticsEmailFor = (
    frequency: TriggerForPersonFrequencyEnum
  ) =>
    triggerStatisticsEmail.mutate(
      { frequency },
      {
        onSuccess: (response) =>
          showToast({
            message: t('triggerStatisticsEmailSuccess', {
              frequency,
              totalSent: response.totalSent,
              organizationsConsidered: response.organizationsConsidered
            })
          })
      }
    );

  const isExtensionDebugFrameEnabled = useAppSettingStore(
    (state) => state.isExtensionDebugFrameEnabled
  );
  const updateIsExtensionDebugFrameEnabled = useAppSettingStore(
    (state) => state.updateIsExtensionDebugFrameEnabled
  );

  const renderColor = (name: string, color: unknown) => (
    <View key={name} style={styles.colorPropertyContainer}>
      {typeof color === 'string' && (
        <View
          style={{
            width: 20,
            height: 20,
            backgroundColor: typeof color === 'string' ? color : undefined
          }}
        />
      )}
      <Text style={styles.colorPropertyText}>{name}</Text>
      {typeof color === 'string' && (
        <Text
          style={[
            styles.colorPropertyText,
            {
              textAlign: 'right',
              fontFamily: 'monospace'
            }
          ]}
        >
          {color}
        </Text>
      )}
    </View>
  );

  return (
    <ScrollView>
      <View style={styles.container}>
        <Text>{t('greeting')}</Text>
        <View style={styles.buttonRow}>
          <Button
            mode="contained"
            buttonColor={theme.colors.error}
            onPress={() => nativeCrash()}
          >
            {t('crash')}
          </Button>
          <Button
            mode="contained"
            buttonColor={theme.colors.error}
            onPress={() => {
              captureMessage('This is a sentry test message', (scope) => {
                scope.setLevel('warning');
                scope.setTag('customEventName', 'Test');
                scope.setTransactionName('TestTransaction');
                return scope;
              });
            }}
          >
            {t('sentryMessage')}
          </Button>
          <Button
            mode="contained"
            buttonColor={theme.colors.error}
            onPress={() => {
              throw new Error('Something went wrong');
            }}
          >
            {t('error')}
          </Button>
          <Button
            mode="contained"
            buttonColor={theme.colors.error}
            onPress={() => alert(debugMessage)}
          >
            {t('debugMessage')}
          </Button>
          <Button
            mode="contained"
            style={styles.button}
            buttonColor={theme.colors.error}
            onPress={() => clearBadges()}
          >
            {t('clearBadges')}
          </Button>
          <Button
            mode="contained"
            style={styles.button}
            buttonColor={theme.colors.tertiary}
            onPress={() =>
              updateBadges(TabRoute.Radio, 12, theme.colors.tertiary)
            }
          >
            {t('setBadges')}
          </Button>
          <NavigationButton
            screen={AppRoute.FeatureSettings}
            mode="contained"
            style={styles.button}
            buttonColor={theme.colors.primary}
          >
            {t('features')}
          </NavigationButton>
          <Button
            mode="contained"
            style={styles.button}
            buttonColor={theme.colors.primary}
            loading={triggerStatisticsEmail.isPending}
            disabled={triggerStatisticsEmail.isPending}
            onPress={() =>
              triggerStatisticsEmailFor(TriggerForPersonFrequencyEnum.Weekly)
            }
          >
            {t('triggerStatisticsEmailWeekly')}
          </Button>
          <Button
            mode="contained"
            style={styles.button}
            buttonColor={theme.colors.primary}
            loading={triggerStatisticsEmail.isPending}
            disabled={triggerStatisticsEmail.isPending}
            onPress={() =>
              triggerStatisticsEmailFor(TriggerForPersonFrequencyEnum.Monthly)
            }
          >
            {t('triggerStatisticsEmailMonthly')}
          </Button>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              backgroundColor: theme.colors.surfaceContainer,
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: 4
            }}
          >
            <Text style={{ color: theme.colors.onSurface }}>
              {t('extensionDebugFrame')}
            </Text>
            <Switch
              value={isExtensionDebugFrameEnabled}
              onValueChange={updateIsExtensionDebugFrameEnabled}
            />
          </View>
        </View>
        <View style={styles.themeColors}>
          {Object.entries(theme.colors).map(([name, color]) =>
            renderColor(name, color)
          )}
          {Object.entries(theme.colors.elevation).map(([name, color]) =>
            renderColor('elevation.' + name, color)
          )}
        </View>
      </View>
    </ScrollView>
  );
};
